<template lang="pug">
	.creative-translations-qa
		Popup(v-if="popupVisible", :popupVisible.sync="popupVisible", :width="960", :height="580", :title="$tCaption('Translations QA')", :closeOnOutsideClick="false")
			.flex-col.space-medium.creative-translations-qa
				Loader(v-if="isSaving", :text="$tCaption('Saving', '@actions')", :fullscreen="true")
				.source.flex-col.space(v-for="source in originalSources")
					.flex-row.space.align-center
						.adt-button.clickable.hoverable.flex-row.align-center.center.padded-xlg.rounded(:hint="$tCaption('Play', '@actions')", @click="playSource(source)")
							fas(:icon="source.__isLoading ? 'spinner' : (source.__playing ? 'fas fa-stop' : 'fas fa-play')", :spin="source.__isLoading")
						b.source-name {{source.__name}}
					.translations.flex-col.space
						.translation.flex-col.space-medium(v-for="item in source.items", :class="{ changed: item.sourceText !== item.text }", :key="item.id", :item-id="item.id")
							.flex-col(v-if="item.originalText")
								.font-mini.upper.grey {{$tCaption('Original text')}}
								.original-text {{item.originalText}}
							.flex-col
								.font-mini.upper.grey {{$tCaption('Translation')}}
								.flex-row.space.align-center.translation-text-area
									.time {{$Data.secondsToDisplayValue(item.track.start)}}
									DxTextArea.flex-1(:value="item.text", stylingMode="filled", valueChangeEvent="input", @value-changed="e => { $set(item, 'text', e.value); $forceUpdate(); }")
									.revert.flex-row.padding.clickable.hoverable(v-if="item.sourceText !== item.text", :title="$tCaption('Revert changes')", @click="item.text = item.sourceText")
										fas(icon="rotate-left")
									.padding-small
										.play.flex-row.padded-xlg.clickable.hoverable.rounded.adt-button.align-center.center(:title="$tCaption('Play changes')", @click="playText(item)")
											fas(:icon="item.__isLoading === 1 ? 'spinner' : (playingItem === item ? 'stop' : 'play')", :spin="!!item.__isLoading")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Cancel', '@actions')", @click="popupVisible = false")
				DxButton(:text="$tCaption('Save', '@actions')", @click="save", type="success", :disabled="!!isSaving || !savingItems.length")
			template(slot="actions-left")
				DxButton(:text="$tCaption('Fix pronunciation')", icon="fas fa-spell-check", @click="$refs.pronunciationTool.show()", :disabled="!selectedText")
		SSMLTextPronunciationTool(ref="pronunciationTool", @selection-change="onPronunciationToolSelectionChange", @change="onPronunciationToolChange")
</template>

<script>
	import _ from 'lodash';
	import Loader from 'app-core/Loader.vue';
	import Popup from 'app-core/Popup.vue';
	import DxTextArea from 'devextreme-vue/text-area';
	import DxButton from 'devextreme-vue/button';
	import ssmlHelper from 'view/insertion/ssml-helper';
	import ssmlGenerator from 'view/creative/ssml-generator';
	import SSMLTextPronunciationTool from 'view/insertion/SSMLTextPronunciationTool.vue';
	import helper from 'view/creative/EasyCreative/helper';
	import cognitiveArrays from 'view/insertion/cognitive-arrays';

	export default {
		translationCategory: 'creative.CreativeTranslationsQA',
		name: 'creative-vast-link',
		props: ['creative', 'tracks', 'creativeDetail'],
		mixins: [cognitiveArrays],
		components: {
			Loader, Popup, DxTextArea, DxButton, SSMLTextPronunciationTool
		},
		computed: {
			savingItems() {
				return this.plainItems.filter(f => f.text && f.text !== f.sourceText);
			},
			plainItems() {
				return this.originalSources.reduce((arr, source) => [...arr, ...source.items], []);
			}
		},
		data() {
			return {
				isSaving: false,
				popupVisible: false,
				originalSources: [],
				playingSource: null,
				filteredSources: null,
				selectedItem: null,
				selectedText: null,
				playingItem: null
			};
		},
		mounted() {
			if (__DEBUG__)
				window.creativeTranslationsQA = this;
		},
		destroyed() {
			this.stop();
		},
		methods: {
			async stopPlayText() {
				if (this.$player) {
					this.$player.pause();
					delete this.$player;
				}
				this.playingVariant = null;
			},	
			async playText(item) {
				if (item.__isLoading === 1) return;

				let hasBeenPlaying = this.playingItem === item;
				this.stopPlayText();

				if (hasBeenPlaying) {
					this.playingItem = null;
					return;
				}

				try {
					await this.loadSpeechVoices(void 0, item?.track?.insertion?.Language);
					this.$set(item, '__isLoading', 1);
					const voice = this.voices.find(f => f.id === item?.track?.insertion?.Voice);

					if (__DEBUG__) {
						console.debug('playText', { voice });
					}

					const ssmlInfo = ssmlHelper.prepareSsml(
						`<speak xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="http://www.w3.org/2001/mstts" xmlns:emo="http://www.w3.org/2009/10/emotionml" version="1.0" xml:lang="${voice?.language?.locale || 'en-US'}">
							<voice name="${voice?.id || 'en-US-JennyNeural'}" xml:lang="${voice?.language?.locale || 'en-US'}">
								<mstts:silence type="Leading-exact" value="0ms"></mstts:silence>
								<prosody rate="1" pitch="+0%">
									${`${item.text}`.replace(/-/g,'')}
								</prosody>
								<mstts:silence type="Tailing-exact" value="0ms"></mstts:silence>
							</voice>
						</speak>`, 
					{
						language: voice?.language?.locale || 'en-US',
						voice: voice?.id || 'en-US-JennyNeural',
					}, true);

						
					const finalVoice = this.voices.find(f => f.id === ssmlInfo.voice);
					const audio = await helper.generateSSML({
						Ssml: ssmlInfo.ssml,
						Rate: 1,
						Language: finalVoice?.language?.locale || 'en-US',
						Voice: finalVoice?.id || 'en-US-JennyNeural',
						JsonConfig: {},
						TtsProviderType: finalVoice?.voice?.TtsProviderType
					})

					if(!audio.base64) return;

					this.$player = new Audio();
					this.$player.src = audio.base64;
					this.$player.play();
					this.$player.addEventListener('ended', () => this.playingItem = null, false);
					this.$player.addEventListener('error', () => this.playingItem = null, false);
					this.playingItem = item;
				} finally {
					this.isVariantLoading = null;
					this.$set(item, '__isLoading', 0);
				}
			},
			onPronunciationToolSelectionChange(e, text) {
				this.selectedItem = this.plainItems.find(f => f.id === document.activeElement.closest('.translation')?.getAttribute('item-id'));
				this.selectedText = text;
			},
			onPronunciationToolChange(text) {
				if (this.selectedItem) {
					this.selectedItem.text = text;
				}
			},
			stop() {
				if (this.$player) {
					this.$player.pause();
					delete this.$player;
				}
				if (this.playingSource) {
					this.$set(this.playingSource, '__playing', false);
					this.playingSource = null;
				}
			},
			async playSource(source) {
				let doBreak = this.playingSource === source;
				this.stop();

				if (doBreak) {
					this.playingSource = null;
					source.__isLoading = false;
					return;
				}
				
				this.playingSource = source;
				this.$set(source, '__isLoading', true);
				try {
					const base64 = await this.$azure.getBlob(source.FileName, void 0, void 0, void 0, source.AzureContainer);
					if (this.playingSource !== source) return;
					this.$player = new Audio();
					this.$player.addEventListener('canplaythrough', () => this.$set(this.playingSource, '__playing', true));
					this.$player.addEventListener('ended', () => this.$set(this.playingSource, '__playing', false));
					this.$player.src = base64;
					this.$player.play();
				} finally {
					source.__isLoading = false;
				}

			},
			show(filteredSources) {
				this.popupVisible = false;
				this.filteredSources = filteredSources;
				this.calcOriginalSources();
				setImmediate(() => {
					this.popupVisible = true;
				});
			},
			async save() {
				const savingItems = this.savingItems;
				if (__DEBUG__)
					console.debug('Save new translation start', { savingItems});
				if (savingItems.length) {
					try {
						this.isSaving++;
						await Promise.all(savingItems.map(async f => {
							const ssml = ssmlHelper.prepareSsml(f.text, this.$Data.formatObjectToLowerCase(f.track.insertion))?.ssml;
							if (__DEBUG__)
								console.debug('Save new translation', { ssml, f });

							if (ssml) {
								f.track.insertion.Ssml = ssml;
								f.track.__saveInsertion();
								ssmlGenerator.generateAndSaveSSMLAudio(this.creativeDetail, f.track, true);
							}
							return true;
						}));
						this.popupVisible = false;
					} finally {
						this.isSaving--;
					}
				}
			},
			calcOriginalSources() {
				const results = [];
				this.tracks.map(f => {
					if (this.filteredSources && !this.filteredSources.find(s => s.Oid === f.insertion.OriginalAudio?.Oid)) return;
					let audio = results.find(r => r.Oid === f.insertion.OriginalAudio?.Oid);
					if (!audio && f.insertion.OriginalAudio) {
						audio = _.cloneDeep(f.insertion.OriginalAudio);
						audio.items = [];
						audio.__name = `${audio.FileName}`.split('_').slice(1,999).join('_') || audio.FileName;
						results.push(audio)
					}
					const div = document.createElement('div');
					div.innerHTML = f.insertion.Ssml;

					const textContent = div.textContent;
					
					let originalText = null;
					if (f.insertion.OriginalSsml) {
						div.innerHTML = f.insertion.OriginalSsml;
						originalText = div.textContent;
					}

					if (audio) {
						audio.items.push({
							track: f,
							text: textContent,
							sourceText: textContent,
							id: f.id,
							originalText
						});
					} else {
						results.push({
							__name: null,
							items: [{
								track: f,
								text: textContent,
								sourceText: textContent,
								originalText,
								id: f.id
							}]
						})
					}
				});

				results.map(f => f.start = _.min(f.items.map(f => f.track.start)));
				this.$set(this, 'originalSources', _.orderBy(results, r => r.start));
			}
		}
	};
</script>
<style lang='scss'>
	.creative-translations-qa {
		.source {
			border-left: 4px solid #efefef;
			padding-left: 8px;

			.translations {
				.translation {
					margin: 2px;
					position: relative;
					border-radius: 6px;

					&.changed {
						.translation-text-area {
							background: #fff5df;
						}
					}

					.time {
						position: absolute;
						right: -6px;
						top: -14px;
						font-size: 9px;
						color: #ccc;
						pointer-events: none;
					}

					.translation-text {
						background: #dedede;
						padding: 4px 4px;
					}
				}
			}
		}
	}
</style>
